import type { ViewComponent } from "@frui.ts/views";
import { registerView } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import React from "react";
import { Card, Graph, HeaderRow, Row, Col, LoaderOverlay } from "@emanprague/ppaskit";
import iconUser from "@emanprague/ppaskit/dist/assets/icons/User.svg";
import iconReceipt from "@emanprague/ppaskit/dist/assets/icons/Receipt.svg";
import StatisticsPageViewModel from "viewModels/admin/statisticsPageViewModel";
import DatePicker from "controls/twoway/DatePicker";
import FilterButton from "controls/filterButton";
import editIcon from "@emanprague/ppaskit/dist/assets/icons/Edit.svg";
import settingsIcon from "@emanprague/ppaskit/dist/assets/icons/Wrench.svg";
import { Input } from "@frui.ts/bootstrap";
import type { AdminStatisticsCard } from "entities/adminStatistics";
import { cloneDeep } from "lodash";
import "./style.scss";
import styles from "./statisticsStyle.module.scss";

interface StatCardProps {
  title: string;
  value: string;
  description: string;
  icon: AdminStatisticsCard["icon"];
}

const StatCard = ({ title, value, description, icon }: StatCardProps) => {
  let iconSrc;
  switch (icon) {
    case "person":
      iconSrc = iconUser;
      break;
    case "receipt":
      iconSrc = iconReceipt;
      break;
  }

  return (
    <Card className={styles.statCard}>
      <Card.Body className={styles.statCardBody}>
        <div className={styles.statCardHeader}>
          <span className={styles.statCardTitle}>{title}</span>
          {iconSrc && <img src={iconSrc} width={20} height={20} className="align-top" />}
        </div>
        <div className={styles.statCardValue}>{value}</div>
        <div className={styles.statCardDescription}>{description}</div>
      </Card.Body>
    </Card>
  );
};

export const statisticsPageView: ViewComponent<StatisticsPageViewModel> = observer(({ vm }) => {
  // Use a local copy of filters to avoid immediate updates on cards
  const [localFilter] = React.useState(cloneDeep(vm.filter));

  return (
    <>
      <HeaderRow level={2} title={vm.translate("title")}>
        <FilterButton
          id="stats-settings"
          confirmLabel={vm.translateGeneral("confirm")}
          // onClear={() => {}}
          onConfirm={() => {
            vm.setFilter({ ...vm.filter, period: localFilter.period });
          }}
          customButton={<img src={settingsIcon} width={24} height={24} style={{ marginLeft: 0, marginRight: 0 }} />}>
          <Row className="align-items-center">
            <Col className={styles.filterContainer}>
              <span className={styles.filterTitle}>{vm.translate("cards.filter_title")}</span>
              <span className={styles.filterDescription}>{vm.translate("cards.filter_description")}</span>
              <Input target={localFilter} property="period" type="number" />
            </Col>
          </Row>
        </FilterButton>
      </HeaderRow>
      <LoaderOverlay loading={vm.busyWatcher.isBusy}>
        {vm.statCardsData && (
          <div className={styles.statCardContainer}>
            {Object.entries(vm.statCardsData).map(([key, item]) => {
              return (
                <StatCard
                  key={key}
                  title={vm.translate(`cards.${key}.title`)}
                  value={vm.formatNumber(item.value)}
                  description={vm.translateCardDescription(key)}
                  icon={item.icon}
                />
              );
            })}
          </div>
        )}

        {vm.lastLoginGraphData && (
          <Card className={styles.graphContainer}>
            <Card.Body>
              <HeaderRow level={4} title={vm.translate("login_graph.title")}>
                <span className={styles.graphDescription}>{vm.translate("login_graph.description")}</span>
              </HeaderRow>
              <Graph labels={vm.lastLoginGraphData.labels} datasets={[{ data: vm.lastLoginGraphData.values }]} />
            </Card.Body>
          </Card>
        )}
        {vm.registrationGraphData && (
          <Card className={styles.graphContainer}>
            <Card.Body>
              <HeaderRow level={4} title={vm.translate("registration_graph.title")}>
                <div className={styles.graphDescriptionContainer}>
                  <span className={styles.graphDescription}>{vm.translate("registration_graph.description")}</span>
                  <strong>
                    {vm.formatDate(vm.filter.registeredFrom!)} - {vm.formatDate(vm.filter.registeredTo!)}
                  </strong>
                  <FilterButton
                    id="dateFilter"
                    confirmLabel={vm.translateGeneral("confirm")}
                    // onClear={() => { }}
                    onConfirm={() => {
                      vm.setFilter({
                        ...vm.filter,
                        registeredFrom: localFilter.registeredFrom,
                        registeredTo: localFilter.registeredTo,
                      });
                    }}
                    customButton={<img src={editIcon} width={20} height={20} style={{ marginLeft: 0, marginRight: 0 }} />}>
                    <Row className="align-items-center">
                      <Col className="mr-n3">
                        <span className={styles.filterTitle}>{vm.translateGeneral("filter_date_from")}</span>
                        <DatePicker
                          target={localFilter}
                          property="registeredFrom"
                          selectsStart
                          minDate={new Date(2020, 1, 1)}
                          maxDate={new Date()}
                        />
                      </Col>
                      <Col>
                        <span className={styles.filterTitle}>{vm.translateGeneral("filter_date_to")}</span>
                        <DatePicker
                          target={localFilter}
                          property="registeredTo"
                          selectsEnd
                          minDate={new Date(2020, 1, 1)}
                          maxDate={new Date()}
                        />
                      </Col>
                    </Row>
                  </FilterButton>
                </div>
              </HeaderRow>
              <Graph labels={vm.registrationGraphData.labels} datasets={[{ data: vm.registrationGraphData.values }]} />
              <div className={styles.graphFooter}>
                {vm.translate("registration_graph.total")}: <b>{vm.formatNumber(vm.registrationGraphData.total ?? 0)}</b>
              </div>
            </Card.Body>
          </Card>
        )}
      </LoaderOverlay>
    </>
  );
});

registerView(statisticsPageView, StatisticsPageViewModel);
